// Central SEO configuration for all pages
// Each page entry ensures unique canonical URLs and meta tags

export interface PageSeoConfig {
  path: string; // Route path without language prefix
  titleKey: string; // i18n key for title
  descriptionKey: string; // i18n key for description
  noindex?: boolean; // For private/auth pages
  priority?: number; // Sitemap priority
}

export const SEO_PAGES: PageSeoConfig[] = [
  // Public pages - paths MUST match App.tsx routes exactly
  { path: '', titleKey: 'meta.home.title', descriptionKey: 'meta.home.description', priority: 1.0 },
  { path: 'about', titleKey: 'meta.about.title', descriptionKey: 'meta.about.description', priority: 0.8 },
  { path: 'examples', titleKey: 'meta.examples.title', descriptionKey: 'meta.examples.description', priority: 0.8 },
  { path: 'faq', titleKey: 'meta.faq.title', descriptionKey: 'meta.faq.description', priority: 0.7 },
  { path: 'help', titleKey: 'meta.help.title', descriptionKey: 'meta.help.description', priority: 0.6 },
  { path: 'contact', titleKey: 'contact.seoTitle', descriptionKey: 'contact.seoDescription', priority: 0.6 },
  { path: 'privacy', titleKey: 'meta.privacy.title', descriptionKey: 'meta.privacy.description', priority: 0.3 },
  // SEO pages - paths match App.tsx routes
  { path: 'vad-ar-e-styling', titleKey: 'seoPages.whatIsEstyling.meta.title', descriptionKey: 'seoPages.whatIsEstyling.meta.description', priority: 0.7 },
  { path: 'what-is-estyling', titleKey: 'seoPages.whatIsEstyling.meta.title', descriptionKey: 'seoPages.whatIsEstyling.meta.description', priority: 0.7 },
  { path: 'digital-vs-traditionell-styling', titleKey: 'seoPages.comparison.meta.title', descriptionKey: 'seoPages.comparison.meta.description', priority: 0.7 },
  { path: 'estyling-comparison', titleKey: 'seoPages.comparison.meta.title', descriptionKey: 'seoPages.comparison.meta.description', priority: 0.7 },
  { path: 'priser-e-styling', titleKey: 'seoPages.pricing.meta.title', descriptionKey: 'seoPages.pricing.meta.description', priority: 0.7 },
  { path: 'estyling-pricing', titleKey: 'seoPages.pricing.meta.title', descriptionKey: 'seoPages.pricing.meta.description', priority: 0.7 },
  { path: 'payments', titleKey: 'meta.payments.title', descriptionKey: 'meta.payments.description', priority: 0.5 },
  
  // Auth pages (public but simple meta)
  { path: 'auth', titleKey: 'meta.auth.title', descriptionKey: 'meta.auth.description', priority: 0.3 },
  { path: 'forgot-password', titleKey: 'meta.forgotPassword.title', descriptionKey: 'meta.forgotPassword.description', priority: 0.2 },
  
  // Private/transient pages - noindex
  { path: 'profile', titleKey: 'meta.profile.title', descriptionKey: 'meta.profile.description', noindex: true },
  { path: 'estyling', titleKey: 'meta.estyling.title', descriptionKey: 'meta.estyling.description', noindex: true },
  { path: 'reset-password', titleKey: 'meta.resetPassword.title', descriptionKey: 'meta.resetPassword.description', noindex: true },
  { path: 'confirm-email', titleKey: 'meta.confirmEmail.title', descriptionKey: 'meta.confirmEmail.description', noindex: true },
  { path: 'payments/success', titleKey: 'meta.paymentSuccess.title', descriptionKey: 'meta.paymentSuccess.description', noindex: true },
  { path: 'payments/cancel', titleKey: 'meta.paymentCancel.title', descriptionKey: 'meta.paymentCancel.description', noindex: true },
];

// Domain configuration
export const DOMAINS = {
  sv: 'https://estylingstudio.se',
  en: 'https://estylingstudio.com',
  da: 'https://estylingstudio.com',
  no: 'https://estylingstudio.com',
  fi: 'https://estylingstudio.com',
} as const;

export const SUPPORTED_LANGUAGES = ['sv', 'en', 'da', 'no', 'fi'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Helper to get domain for language
export const getDomainForLang = (lang: string): string => {
  return DOMAINS[lang as SupportedLanguage] ?? DOMAINS.en;
};

// Helper to build canonical URL - always returns unique URL per language/path combo
export const buildCanonicalUrl = (lang: string, path: string = ''): string => {
  const domain = getDomainForLang(lang);
  const cleanPath = path.replace(/^\/+/, '').replace(/\/+$/, '');
  if (!cleanPath) {
    return `${domain}/${lang}/`;
  }
  return `${domain}/${lang}/${cleanPath}/`;
};

// Get SEO config for a path
export const getSeoConfigForPath = (path: string): PageSeoConfig | undefined => {
  const cleanPath = path.replace(/^\/+/, '').replace(/\/+$/, '');
  return SEO_PAGES.find(page => page.path === cleanPath);
};
